import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _excluded = ["onChange", "disabled", "style"],
    _excluded2 = ["arrow", "autofill", "disabled", "error", "fieldTooltip", "fieldTooltipDoNotFold", "focused", "hasDegradedPerformance", "hasValue", "ids", "isUnderlined", "label", "loading", "name", "onActive", "onDisabledClick", "obfuscatedValue", "value", "styleSheet", "warning", "UNSAFE_inputProps"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import React from 'react';
import { elegir } from 'elegir';
import Color from '@bubble-dev/fantasy-color';
import { compose, setDisplayName, withProps } from 'recompose';
import { withUncontrolledProp } from '@klarna/higher-order-components';
import omitProps from '@hocs/omit-props';
import * as ParticleBorderRadii from '@bubble-ui/particle-border-radii';
import { ColorFunctions as Colors } from '@bubble-ui/particle-colors';
import { isDarkMode } from '@bubble-ui/particle-design-versions';
import * as ParticleDesignVersions from '@bubble-ui/particle-design-versions';
import { withDesignVersion } from '@bubble-ui/provider-design-version';
import * as FontSizes from '@bubble-ui/particle-font-sizes';
import * as FontFamilies from '@bubble-ui/particle-font-families';
import * as Layers from '@bubble-ui/particle-layers';
import View from '@bubble-ui/primitive-view';
import baseField from '@bubble-ui/temp-base-field';
import LabelParticle from '@bubble-ui/atom-label';
import FieldTooltipParticle from '@bubble-ui/temp-field-tooltip';
import InputParticle from '@bubble-ui/temp-input';
import wrapOverridableComponent from '@bubble-ui/hoc-wrap-overridable-component';
import { platformSelect } from '@bubble-ui/util-platform';
import { Motion, spring } from '@bubble-ui/primitive-motion';
import Loader from '@bubble-ui/molecule-loader';
import withPropDeprecationWarning from '@bubble-ui/hoc-with-prop-deprecation-warning';

var caretColor = function caretColor(designVersion) {
  return isDarkMode(designVersion) ? '#51839C' : '#487B94';
};

var defaultInputColor = function defaultInputColor(designVersion) {
  return isDarkMode(designVersion) ? '#FFFFFF' : '#17120F';
};

var disabledLabel = function disabledLabel(designVersion) {
  return isDarkMode(designVersion) ? '#969492' : '#787471';
};

var errorTextColor = function errorTextColor(designVersion) {
  return isDarkMode(designVersion) ? '#FFFFFF' : '#17120F';
};

var warningTextColor = function warningTextColor(designVersion) {
  return isDarkMode(designVersion) ? '#FFFFFF' : '#17120F';
};

var placeholderColor = function placeholderColor(designVersion) {
  return isDarkMode(designVersion) ? '#969492' : '#787471';
};

var getDefaultStyleSheet = function getDefaultStyleSheet(_ref) {
  var autofill = _ref.autofill,
      darkBackground = _ref.darkBackground,
      designVersion = _ref.designVersion,
      disabled = _ref.disabled,
      error = _ref.error,
      fieldTooltip = _ref.fieldTooltip,
      focused = _ref.focused,
      isUnderlined = _ref.isUnderlined,
      hasLabel = _ref.hasLabel,
      hasValue = _ref.hasValue,
      _ref$height = _ref.height,
      height = _ref$height === void 0 ? '100%' : _ref$height,
      hovered = _ref.hovered,
      icon = _ref.icon,
      obfuscatedValue = _ref.obfuscatedValue,
      position = _ref.position,
      squareBottomBorder = _ref.squareBottomBorder,
      stack = _ref.stack,
      tooltipActive = _ref.tooltipActive,
      warning = _ref.warning,
      width = _ref.width;
  return {
    rootStyle: _objectSpread({
      flexGrow: 1,
      flexShrink: 1
    }, platformSelect({
      web: {
        // so this is assuming that we don't use
        // fieldsets with Field in the native context, only
        // fieldsets with Inputs. Nowadays, Android is losing focus
        // on the field if the zindex is changed
        zIndex: elegir( // field tooltip active
        tooltipActive, Layers.TOOLTIP, // focused
        focused, Layers.FOCUS, // error
        error, Layers.ERROR, // warning
        warning, Layers.WARNING, // hovered
        hovered, Layers.HOVER // default: none
        )
      },
      default: {}
    })),
    inputStyle: _objectSpread(_objectSpread({
      boxShadow: 'none',
      caretColor: caretColor(designVersion),
      color: elegir( // obfuscatedValue
      obfuscatedValue && obfuscatedValue !== '', Colors.transparent(designVersion).toString(), // disabled
      disabled, disabledLabel(designVersion), // error
      error, errorTextColor(designVersion), // warning
      warning, warningTextColor(designVersion), // default
      true, defaultInputColor(designVersion))
    }, designVersion === ParticleDesignVersions.KUZCO ? FontFamilies.KLARNA_TEXT_MEDIUM : FontFamilies.KLARNA_SANS_MEDIUM), {}, {
      fontSize: FontSizes.SIZE_DEFAULT,
      height: '100%',
      opacity: disabled ? 0.5 : 1,
      paddingLeft: 0,
      paddingTop: hasLabel ? 20 : 0,
      textOverflow: 'ellipsis',
      top: 0,
      resize: 'none',
      marginTop: platformSelect({
        ios: 0,
        android: 5,
        web: 0
      }),
      width: '100%'
    }),
    containerStyle: _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({
      alignItems: 'stretch'
    }, isUnderlined ? {
      borderBottomWidth: darkBackground ? 0 : 1
    } : {
      borderWidth: darkBackground ? 0 : 1
    }), {}, {
      borderStyle: 'solid',
      boxSizing: 'border-box',
      flexDirection: 'row',
      height: height,
      justifyContent: 'space-between',
      paddingTop: 15,
      paddingLeft: 15,
      paddingRight: elegir( // tooltip
      Boolean(fieldTooltip), isUnderlined ? 20 : 35, // default
      true, 15),
      paddingBottom: 15,
      position: 'relative',
      backgroundColor: elegir( // disabled
      disabled, isDarkMode(designVersion) ? '#333130' : '#F0EEEA', // error & focused
      error && focused, isDarkMode(designVersion) ? '#121111' : '#FFFFFF', // error
      error, isDarkMode(designVersion) ? '#17120F' : '#FFF6F5', // warning & focused
      warning && focused, isDarkMode(designVersion) ? '#121111' : '#FFFFFF', // warning
      warning, isDarkMode(designVersion) ? '#121111' : '#FFFBF0', // autofill
      autofill, 'rgba(236, 245, 92, 0.1)', // dark background
      darkBackground, isDarkMode(designVersion) ? '#121111' : '#FFFFFF', // default
      true, Colors.transparent(designVersion)),
      borderColor: elegir( // disabled
      disabled, '#D1D0CE', // error & hover
      error && hovered, isDarkMode(designVersion) ? '#C2190A' : '#E64637', // error
      error, isDarkMode(designVersion) ? '#E01D0B' : '#FA4C3C', // warning & hover
      warning && hovered, isDarkMode(designVersion) ? '#875700' : '#B57600', // warning
      warning, isDarkMode(designVersion) ? '#A16800' : '#C98300', // autofill
      autofill, '#CFD74A', // hovered
      hovered, '#787471', // focused
      focused, isDarkMode(designVersion) ? '#FFFFFF' : '#17120F', // default
      true, '#D1D0CE')
    }, elegir(isUnderlined, {
      borderRadius: 0
    }, // bottomRight
    position === 'bottomRight', {
      borderBottomRightRadius: ParticleBorderRadii.SMALL
    }, // bottomLeft
    position === 'bottomLeft', {
      borderBottomLeftRadius: ParticleBorderRadii.SMALL
    }, // bottom
    position === 'bottom', {
      borderBottomLeftRadius: ParticleBorderRadii.SMALL,
      borderBottomRightRadius: ParticleBorderRadii.SMALL
    }, // centerLeft || center || centerRight || inner
    position === 'centerLeft' || position === 'center' || position === 'centerRight' || position === 'inner', {}, // right
    position === 'right', {
      borderBottomRightRadius: ParticleBorderRadii.SMALL,
      borderTopRightRadius: ParticleBorderRadii.SMALL
    }, // left
    position === 'left', {
      borderBottomLeftRadius: ParticleBorderRadii.SMALL,
      borderTopLeftRadius: ParticleBorderRadii.SMALL
    }, // topRight
    position === 'topRight', {
      borderTopRightRadius: ParticleBorderRadii.SMALL
    }, // topLeft
    position === 'topLeft', {
      borderTopLeftRadius: ParticleBorderRadii.SMALL
    }, // top
    position === 'top', {
      borderTopLeftRadius: ParticleBorderRadii.SMALL,
      borderTopRightRadius: ParticleBorderRadii.SMALL
    }, // default
    position === 'standalone', {
      borderRadius: ParticleBorderRadii.SMALL
    })), squareBottomBorder ? {
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0
    } : {}), {}, {
      width: elegir(position === 'inner' || // position in the right
      position === 'topRight' || position === 'right' || position === 'centerRight' || position === 'bottomRight', width + 1, // default
      true, width)
    }, elegir(position === 'inner' || // position in the right
    position === 'topRight' || position === 'right' || position === 'centerRight' || position === 'bottomRight', {
      marginLeft: -1
    }, // default
    true, {})), stack ? {
      marginTop: -1
    } : {}),
    labelTextStyle: function labelTextStyle(fontSize) {
      return _objectSpread(_objectSpread({
        color: elegir( // disabled
        disabled, disabledLabel(designVersion), // error
        error, errorTextColor(designVersion), // warning
        warning, warningTextColor(designVersion), // default
        true, placeholderColor(designVersion))
      }, designVersion === ParticleDesignVersions.KUZCO ? FontFamilies.KLARNA_TEXT_REGULAR : FontFamilies.KLARNA_SANS_REGULAR), {}, {
        fontSize: fontSize,
        lineHeight: 30,
        overflow: 'hidden',
        textAlign: 'left',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        width: '100%'
      });
    },
    labelViewStyle: function labelViewStyle(marginTop) {
      return {
        backgroundColor: Colors.transparent(designVersion).toString(),
        opacity: disabled ? 0.5 : 1,
        pointerEvents: 'none',
        marginTop: platformSelect({
          web: marginTop,
          android: marginTop + 5,
          ios: marginTop + 5
        }),
        width: '100%'
      };
    },
    iconColorStyle: _objectSpread({}, icon ? _objectSpread({}, icon.props.colorToken ? {
      colorToken: icon.props.colorToken,
      colorTokenArgs: icon.props.colorTokenArgs
    } : icon.props.color ? {
      color: icon.props.color
    } : {
      colorToken: error && focused ? 'colors/text/error' : warning && focused ? 'colors/text/warning' : focused || hasValue || error || warning ? 'colors/text/default' : 'colors/text/neutral'
    }) : {}),
    iconStyle: {
      alignSelf: 'center',
      height: 20,
      paddingRight: 15
    },
    loaderStyle: {
      alignSelf: 'center',
      paddingLeft: 5
    },
    textWrapperStyle: {
      flexDirection: 'column',
      flexGrow: 0,
      flexShrink: 1,
      position: 'relative',
      width: '100%'
    }
  };
};

var TextAreaInput = function TextAreaInput(_ref2) {
  var onChange = _ref2.onChange,
      disabled = _ref2.disabled,
      style = _ref2.style,
      props = _objectWithoutProperties(_ref2, _excluded);

  return /*#__PURE__*/React.createElement("textarea", _extends({
    onChange: onChange,
    disabled: disabled,
    style: style
  }, props));
};

var TextArea = function TextArea(_ref3) {
  var arrow = _ref3.arrow,
      autofill = _ref3.autofill,
      disabled = _ref3.disabled,
      error = _ref3.error,
      fieldTooltip = _ref3.fieldTooltip,
      fieldTooltipDoNotFold = _ref3.fieldTooltipDoNotFold,
      focused = _ref3.focused,
      hasDegradedPerformance = _ref3.hasDegradedPerformance,
      hasValue = _ref3.hasValue,
      ids = _ref3.ids,
      isUnderlined = _ref3.isUnderlined,
      label = _ref3.label,
      loading = _ref3.loading,
      name = _ref3.name,
      onActive = _ref3.onActive,
      onDisabledClick = _ref3.onDisabledClick,
      obfuscatedValue = _ref3.obfuscatedValue,
      value = _ref3.value,
      _ref3$styleSheet = _ref3.styleSheet,
      rootStyle = _ref3$styleSheet.rootStyle,
      containerStyle = _ref3$styleSheet.containerStyle,
      inputStyle = _ref3$styleSheet.inputStyle,
      labelViewStyle = _ref3$styleSheet.labelViewStyle,
      labelTextStyle = _ref3$styleSheet.labelTextStyle,
      loaderStyle = _ref3$styleSheet.loaderStyle,
      textWrapperStyle = _ref3$styleSheet.textWrapperStyle,
      warning = _ref3.warning,
      UNSAFE_inputProps = _ref3.UNSAFE_inputProps,
      props = _objectWithoutProperties(_ref3, _excluded2);

  var targetBorderColor = typeof containerStyle.borderColor === 'string' ? new Color(containerStyle.borderColor) : containerStyle.borderColor;
  var targetBackgroundColor = typeof containerStyle.backgroundColor === 'string' ? new Color(containerStyle.backgroundColor) : containerStyle.backgroundColor;
  return /*#__PURE__*/React.createElement(Motion, {
    style: {
      backgroundColorR: spring(targetBackgroundColor.red),
      backgroundColorG: spring(targetBackgroundColor.green),
      backgroundColorB: spring(targetBackgroundColor.blue),
      backgroundColorA: spring(targetBackgroundColor.alpha),
      borderColorR: spring(targetBorderColor.red),
      borderColorG: spring(targetBorderColor.green),
      borderColorB: spring(targetBorderColor.blue)
    }
  }, function (_ref4) {
    var backgroundColorR = _ref4.backgroundColorR,
        backgroundColorG = _ref4.backgroundColorG,
        backgroundColorB = _ref4.backgroundColorB,
        backgroundColorA = _ref4.backgroundColorA,
        borderColorR = _ref4.borderColorR,
        borderColorG = _ref4.borderColorG,
        borderColorB = _ref4.borderColorB,
        borderColorA = _ref4.borderColorA;
    return /*#__PURE__*/React.createElement(View, {
      id: ids.root,
      style: rootStyle
    }, /*#__PURE__*/React.createElement(View, {
      id: ids.container,
      tag: "label",
      accessibilityTraits: disabled ? ['disabled'] : undefined,
      accessibilityState: {
        disabled: disabled
      },
      htmlFor: ids.input || name,
      onClick: function onClick(e) {
        return disabled && onDisabledClick && onDisabledClick(e);
      },
      style: _objectSpread(_objectSpread({}, containerStyle), {}, {
        backgroundColor: "rgba(".concat(Math.round(backgroundColorR), ", ").concat(Math.round(backgroundColorG), ", ").concat(Math.round(backgroundColorB), ", ").concat(backgroundColorA, ")")
      }, isUnderlined ? {
        borderBottomColor: "rgba(".concat(Math.round(borderColorR), ", ").concat(Math.round(borderColorG), ", ").concat(Math.round(borderColorB), ", ").concat(borderColorA, ")")
      } : {
        borderColor: "rgb(".concat(Math.round(borderColorR), ", ").concat(Math.round(borderColorG), ", ").concat(Math.round(borderColorB), ")")
      })
    }, /*#__PURE__*/React.createElement(View, {
      id: ids.textWrapper,
      style: textWrapperStyle
    }, label && label !== '' && /*#__PURE__*/React.createElement(LabelParticle, {
      hasValue: hasValue || obfuscatedValue && obfuscatedValue !== '' || hasDegradedPerformance,
      focused: focused || error || warning || autofill,
      id: ids.label,
      viewStyle: labelViewStyle,
      textStyle: labelTextStyle
    }, label), /*#__PURE__*/React.createElement(InputParticle, _extends({}, UNSAFE_inputProps, {
      "aria-label": label,
      disabled: disabled,
      id: ids.input || name,
      name: name,
      style: inputStyle,
      value: obfuscatedValue && obfuscatedValue !== '' ? obfuscatedValue : value,
      Input: TextAreaInput
    }, props))), loading && /*#__PURE__*/React.createElement(View, {
      id: ids.loader,
      style: loaderStyle
    }, /*#__PURE__*/React.createElement(Loader, null))), fieldTooltip && /*#__PURE__*/React.createElement(FieldTooltipParticle, {
      id: ids.fieldTooltip,
      arrow: arrow,
      error: error,
      fieldTooltipDoNotFold: fieldTooltipDoNotFold,
      fieldTooltip: fieldTooltip,
      onActive: onActive,
      warning: warning,
      withPadding: !isUnderlined
    }));
  });
};

export default compose(setDisplayName('TextArea'), baseField, withProps(function (_ref5) {
  var position = _ref5.position;
  return {
    position: position || 'standalone'
  };
}), withUncontrolledProp({
  prop: 'tooltipActive',
  handlers: {
    onActive: function onActive(props) {
      return function (isActive) {
        return props.tooltipActive = isActive;
      };
    }
  }
}), withDesignVersion, wrapOverridableComponent('TextArea', getDefaultStyleSheet), omitProps('designVersion', 'darkBackground', 'hasLabel', 'hovered', 'id', 'onAutofill', 'onEndAutofill', 'pressed', 'position', 'stack', 'squareBottomBorder', 'tooltipActive', 'isObfuscated', 'width', 'inputRef'), withPropDeprecationWarning({
  name: 'big',
  removalDate: 'Q1 2022'
}), withPropDeprecationWarning({
  name: 'fieldTooltipDoNotFold',
  removalDate: 'v5.0.0'
}))(TextArea);
//# sourceMappingURL=index.js.map